import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `MeshStandardNodeMaterial` asset: metal/rough PBR with serialized metalness and roughness. */
@EngineClass('MeshStandardNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Mesh Standard Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class MeshStandardNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Diffuse color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Metalness' })
  override metalness = 0.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Roughness' })
  override roughness = 0.5;

  @property({ type: 'boolean', description: 'Enable flat shading' })
  override flatShading = false;

  @property({ type: 'color', description: 'Emissive color' })
  override emissive = new THREE.Color(0.0, 0.0, 0.0);

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Emissive intensity' })
  override emissiveIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Ambient occlusion map intensity' })
  override aoMapIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Light map intensity' })
  override lightMapIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Environment reflectivity' })
    reflectivity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Refraction ratio' })
    refractionRatio = 0.98;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Environment map intensity' })
  override envMapIntensity = 1.0;

  @property({ type: 'number', min: -10, max: 10, step: 0.01, description: 'Bump map influence' })
  override bumpScale = 1.0;

  @property({ type: 'vector2', description: 'Normal map influence' })
  override normalScale = new THREE.Vector2(1, 1);

  @property({ type: 'number', description: 'Normal map type (`THREE.TangentSpaceNormalMap`, `ObjectSpaceNormalMap`)' })
  override normalMapType = THREE.TangentSpaceNormalMap;

  @property({ type: 'texturePath', description: 'Color map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Alpha map path' })
    alphaMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Roughness map path' })
    roughnessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Metalness map path' })
    metalnessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Normal map path' })
    normalMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Bump map path' })
    bumpMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Displacement map path' })
    displacementMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Emissive map path' })
    emissiveMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Ambient occlusion map path' })
    aoMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Environment map path' })
    envMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Light map path' })
    lightMapPath: TexturePath = '';

  private readonly _metalnessUniform = TSL.uniform(this.metalness);
  private readonly _roughnessUniform = TSL.uniform(this.roughness);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.metalness = this.clampFiniteNumber(this.metalness, 0, 1, 0.0);
    this.roughness = this.clampFiniteNumber(this.roughness, 0, 1, 0.5);
    this.emissiveIntensity = this.floorFiniteNumber(this.emissiveIntensity, 0, 1.0);
    this.aoMapIntensity = this.clampFiniteNumber(this.aoMapIntensity, 0, 1, 1.0);
    this.lightMapIntensity = this.floorFiniteNumber(this.lightMapIntensity, 0, 1.0);
    this.reflectivity = this.clampFiniteNumber(this.reflectivity, 0, 1, 1.0);
    this.refractionRatio = this.clampFiniteNumber(this.refractionRatio, 0, 1, 0.98);
    this.envMapIntensity = this.floorFiniteNumber(this.envMapIntensity, 0, 1.0);
    this.bumpScale = this.clampFiniteNumber(this.bumpScale, -10, 10, 1.0);
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace);
    this.syncTexturePath('roughnessMap', this.roughnessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('metalnessMap', this.metalnessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('normalMap', this.normalMapPath, THREE.NoColorSpace);
    this.syncTexturePath('bumpMap', this.bumpMapPath, THREE.NoColorSpace);
    this.syncTexturePath('displacementMap', this.displacementMapPath, THREE.NoColorSpace);
    this.syncTexturePath('emissiveMap', this.emissiveMapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('aoMap', this.aoMapPath, THREE.NoColorSpace);
    this.syncTexturePath('envMap', this.envMapPath, THREE.NoColorSpace);
    this.syncTexturePath('lightMap', this.lightMapPath, THREE.NoColorSpace);
    this.colorNode = null;
    this._metalnessUniform.value = this.metalness;
    this._roughnessUniform.value = this.roughness;
    this.metalnessNode = this._metalnessUniform;
    this.roughnessNode = this._roughnessUniform;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): MeshStandardNodeMaterialAsset {
    const instance = new MeshStandardNodeMaterialAsset();
    MeshStandardNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
